import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IdType, CreateMonitoredIdentityDto } from '../services';

interface AddMonitoredIdentityFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateMonitoredIdentityDto) => Promise<void>;
  initialData?: CreateMonitoredIdentityDto | null;
}

const AddMonitoredIdentityForm: React.FC<AddMonitoredIdentityFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData
}) => {
  const { t } = useTranslation();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{nationalId?: string, idType?: string, submit?: string}>({});
  const [formData, setFormData] = useState<CreateMonitoredIdentityDto>({
    nationalId: '',
    idType: IdType.PersonalId
  });

  // Update form data when initialData changes
  React.useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({
        nationalId: '',
        idType: IdType.PersonalId
      });
    }
  }, [initialData]);

  const handleClose = () => {
    if (isSubmitting) return;
    setFormData({
      nationalId: '',
      idType: IdType.PersonalId
    });
    setErrors({});
    onClose();
  };

  const validateForm = (): boolean => {
    const newErrors: {nationalId?: string, idType?: string, submit?: string} = {};

    // Validate national ID
    if (!formData.nationalId.trim()) {
      newErrors.nationalId = t('validation.cedulaRequired');
    } else {
      const normalizedId = formData.nationalId.replace(/[-\s]/g, '');
      
      if (formData.idType === IdType.PersonalId) {
        if (!/^\d{9}$/.test(normalizedId)) {
          newErrors.nationalId = t('validation.cedulaPersonalInvalid');
        }
      } else if (formData.idType === IdType.CompanyId) {
        if (!/^3\d{9}$/.test(normalizedId)) {
          newErrors.nationalId = t('validation.cedulaCompanyInvalid');
        }
      } else if (formData.idType === IdType.DIMEX) {
        if (!/^\d{12}$/.test(normalizedId)) {
          newErrors.nationalId = t('validation.dimexInvalid');
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData: CreateMonitoredIdentityDto = {
        nationalId: formData.nationalId.replace(/[-\s]/g, ''), // Remove dashes and spaces
        idType: formData.idType
      };

      await onSubmit(submitData);
      handleClose();
    } catch (error) {
      setErrors(prev => ({ 
        ...prev, 
        submit: t('messages.error') || 'An error occurred while saving. Please try again.' 
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIdTypeChange = (newIdType: IdType) => {
    setFormData(prev => ({
      ...prev,
      idType: newIdType
    }));
    
    // Clear errors when changing ID type
    setErrors(prev => ({ ...prev, idType: undefined, submit: undefined }));
  };

  const handleNationalIdChange = (value: string) => {
    setFormData(prev => ({ ...prev, nationalId: value }));
    if (errors.nationalId || errors.submit) {
      setErrors(prev => ({ ...prev, nationalId: undefined, submit: undefined }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {t('identities.addNew')}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {errors.submit && (
              <Alert severity="error">
                {errors.submit}
              </Alert>
            )}
            <FormControl fullWidth required error={!!errors.idType}>
              <InputLabel>{t('registration.idTypeLabel')}</InputLabel>
              <Select
                value={formData.idType}
                label={t('registration.idTypeLabel')}
                onChange={(e) => handleIdTypeChange(e.target.value as IdType)}
                disabled={isSubmitting}
              >
                <MenuItem value={IdType.PersonalId}>{t('registration.personalId')}</MenuItem>
                <MenuItem value={IdType.CompanyId}>{t('registration.companyId')}</MenuItem>
                <MenuItem value={IdType.DIMEX}>{t('idType.dimex')}</MenuItem>
              </Select>
              {errors.idType && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {errors.idType}
                </Alert>
              )}
            </FormControl>

            {formData.idType === IdType.CompanyId && (
              <Alert severity="info" sx={{ mb: 1 }}>
                {t('register.companyIdWarning')}
              </Alert>
            )}

            <TextField
              fullWidth
              required
              label={t('register.cedulaLabel')}
              placeholder={
                formData.idType === IdType.PersonalId
                  ? t('register.cedulaPersonalPlaceholder')
                  : formData.idType === IdType.CompanyId
                  ? t('register.cedulaCompanyPlaceholder')
                  : t('register.dimexPlaceholder')
              }
              value={formData.nationalId}
              onChange={(e) => handleNationalIdChange(e.target.value)}
              error={!!errors.nationalId}
              helperText={errors.nationalId}
              disabled={isSubmitting}
              slotProps={{ htmlInput: { maxLength: 50 } }}
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button 
            type="submit" 
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t('messages.saving')}
              </>
            ) : (
              t('identities.add')
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddMonitoredIdentityForm;